import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../../hooks';
import {
  IBreadcrumb,
  ICurrency,
  ILanguage,
  IMainNavBar,
} from '../../../models/strapi.models';
import { scrollToContactUs } from '../../../shared/useCommon';
import { savePostLoginRedirect } from '../../../shared/usePostLoginRedirect';
import { getCurrencySymbol } from '../../../shared/useUtils';
import { IAppPage } from '../../../store/slices/appSlice';
import { NavbarStyle } from '../../../styles/components/common/layout';
import { Button } from '../../common/ui';
import NavBarDropdown, { INavBarDropdownProps } from './NavBarDropdown';

export interface INavBarProps {
  items?: IMainNavBar;
  appPages: IAppPage[];
  appLanguages: ILanguage[];
  appCurrencies: ICurrency[];
  appLocale: string | undefined;
  appCurrentCurrency?: ICurrency | undefined;
  canShow: boolean;
  breadcrumbsHomePageDisplayName?: string;
  breadcrumbsCurrentPageDisplayName?: string;
  breadcrumbAncestors?: IBreadcrumb[];
  onUserLogout?: () => Promise<void>;
  onLocaleChange?: (
    languageCode: string,
    source: 'use-effect' | 'nav-bar',
    route?: { path: string; slug?: string }
  ) => void;
  onCurrencyChange?: (currency: ICurrency) => void;
}

const NavBar = (props: INavBarProps) => {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const user = useAppSelector((state) => state.auth.user);
  const appState = useAppSelector((state) => state.app);
  const router = useRouter();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [mobileMenuItemsCollapsedState, setMobileMenuItemsCollapsedState] =
    useState({
      account: true,
      services: true,
      pricing: true,
      resources: true,
      about: true,
      language: true,
      currency: true,
    });

  const getRoute = useCallback(
    (actualPath: string) => {
      return (
        props.appPages?.find(
          (x: IAppPage) =>
            x && x.destination === actualPath && x.language === router.locale
        )?.source || actualPath
      );
    },
    [props.appPages, router.locale]
  );

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
    setTimeout(() => {
      document
        .querySelector(`.${NavbarStyle['navbar-mobile-scroller']}`)
        ?.scrollTo({ top: 0 });
    }, 400);
  };

  const getSelectableLanguageLabel = (language: ILanguage | undefined) => {
    const label =
      language?.nameTranslations?.find((x) => x.language === props.appLocale)
        ?.text ||
      language?.nameTranslations?.find((x) => x.language === 'en')?.text;

    return label;
  };

  const handleDropDownToggle = (event: any) => {
    const element: HTMLDetailsElement = document.getElementById(
      event.target.id
    ) as HTMLDetailsElement;
    const dropDownContent = element?.querySelector(
      `.${NavbarStyle['dropdown-content']}`
    );
    element?.open && dropDownContent
      ? dropDownContent?.classList.add(NavbarStyle['dropdown-content-animated'])
      : dropDownContent?.classList.remove(
          NavbarStyle['dropdown-content-animated']
        );
  };

  const handleDropDownLeaveToggle = (event: any) => {
    const detailsElement = event
      .composedPath()
      .find((x: any) => x.localName === 'details');
    detailsElement.removeAttribute('open');
  };

  const handleDropDownLeaveToggleAll = (event: any) => {
    const elements: NodeListOf<HTMLDetailsElement> =
      document.querySelectorAll('details');

    elements.forEach((x) => {
      if (x.hasAttribute('open')) {
        x.removeAttribute('open');
      }
    });
  };

  const registerHandleToggleAll = useCallback(() => {
    document.addEventListener('click', (event: any) => {
      handleDropDownLeaveToggleAll(event);
    });
  }, []);

  const registerHandleDropDownLeaveToggle = useCallback(() => {
    const dropDownsItems = document.querySelectorAll(
      `.${NavbarStyle['dropdown-content']} li`
    );
    dropDownsItems.forEach((x) =>
      x.addEventListener('click', handleDropDownLeaveToggle)
    );
  }, []);

  const handleLanguageChange = (language: ILanguage) => {
    const languageDropdownElement =
      document.getElementById('language-dropdown');
    languageDropdownElement?.removeAttribute('open');
    if (!!props.onLocaleChange) {
      props.onLocaleChange(language.twoLetterIso as string, 'nav-bar', {
        path: router.pathname,
        slug: router.query.slug as string,
      });
    }
  };

  const handleCurrencyChange = (currency: ICurrency) => {
    if (!!props.onCurrencyChange) {
      props.onCurrencyChange(currency);
    }
    const currencyDropdownElement =
      document.getElementById('currency-dropdown');
    currencyDropdownElement?.removeAttribute('open');
  };

  const handleLoginClick = useCallback(() => {
    // Save current page for post-login redirect
    const currentPath =
      router.locale !== 'en'
        ? `/${router.locale}${router.asPath}`
        : `${router.asPath}`;
    savePostLoginRedirect(currentPath);
  }, [router.asPath, router.locale]);

  const toggleAccountItems = () => {
    setMobileMenuItemsCollapsedState({
      ...mobileMenuItemsCollapsedState,
      account: !mobileMenuItemsCollapsedState.account,
    });
  };

  const toggleServicesItems = () => {
    setMobileMenuItemsCollapsedState({
      ...mobileMenuItemsCollapsedState,
      services: !mobileMenuItemsCollapsedState.services,
    });
  };

  const togglePricingItems = () => {
    setMobileMenuItemsCollapsedState({
      ...mobileMenuItemsCollapsedState,
      pricing: !mobileMenuItemsCollapsedState.pricing,
    });
  };

  const toggleResourcesItems = () => {
    setMobileMenuItemsCollapsedState({
      ...mobileMenuItemsCollapsedState,
      resources: !mobileMenuItemsCollapsedState.resources,
    });
  };

  const toggleAboutItems = () => {
    setMobileMenuItemsCollapsedState({
      ...mobileMenuItemsCollapsedState,
      about: !mobileMenuItemsCollapsedState.about,
    });
  };

  const toggleLanguageItems = () => {
    setMobileMenuItemsCollapsedState({
      ...mobileMenuItemsCollapsedState,
      language: !mobileMenuItemsCollapsedState.language,
    });
  };

  const toggleCurrencyItems = () => {
    setMobileMenuItemsCollapsedState({
      ...mobileMenuItemsCollapsedState,
      currency: !mobileMenuItemsCollapsedState.currency,
    });
  };

  const goToContactUs = () => {
    const scrolled = scrollToContactUs();
    if (!scrolled) {
      router.push(`${getRoute('/contact-us')}?source=${window.location.href}`);
    }
  };

  const breadcrumbs = useMemo(() => {
    let collection: IBreadcrumb[] = [
      {
        label: props.breadcrumbsHomePageDisplayName || 'Home',
        path: `/${router.locale !== 'en' ? router.locale : ''}`,
      },
    ];

    if (props.breadcrumbAncestors && props.breadcrumbAncestors.length > 0) {
      collection = [...collection, ...props.breadcrumbAncestors];
    }

    return [
      ...collection,
      { label: props.breadcrumbsCurrentPageDisplayName || '-' },
    ];
  }, [
    props.breadcrumbAncestors,
    props.breadcrumbsHomePageDisplayName,
    props.breadcrumbsCurrentPageDisplayName,
    router.locale,
  ]);

  const servicesProps: INavBarDropdownProps = {
    id: 'services-group-dropdown',
    label: props.items?.servicesLabel || 'Services',
    groups: [
      {
        title: props.items?.trademarkServicesGroupLabel || 'Trademark Services',
        path: props.items?.trademarkServicesGroupPath || '/services/trademark',
        items: props.items?.trademarkServicesGroupItems || [],
      },
      {
        title: props.items?.otherIpServicesGroupLabel || 'Other IP Services',
        items: props.items?.otherIpServicesGroupItems || [],
      },
    ],
    onToggle: handleDropDownToggle,
  };

  const pricingProps: INavBarDropdownProps = {
    id: 'pricing-dropdown',
    label: props.items?.pricingLabel || 'Pricing',
    items: props.items?.pricingItems || [],
    onToggle: handleDropDownToggle,
  };

  const resourcesProps: INavBarDropdownProps = {
    id: 'resources-group-dropdown',
    label: props.items?.resourcesLabel || 'Resources',
    groups: [
      {
        title:
          props.items?.intellectualPropertyQuestionGroupLabel ||
          'Intellectual Property Questions',
        path:
          props.items?.intellectualPropertyQuestionGroupPath ||
          '/resources/intellectual-property-questions',
        items: props.items?.intellectualPropertyQuestionGroupItems || [],
      },
      {
        title: props.items?.otherResourcesGroupLabel || 'Other Resources',
        items: props.items?.otherResourcesGroupItems || [],
      },
    ],
    onToggle: handleDropDownToggle,
  };

  const aboutProps: INavBarDropdownProps = {
    id: 'about-dropdown',
    label: props.items?.aboutUsLabel || 'About',
    items: props.items?.aboutUsItems || [],
    onToggle: handleDropDownToggle,
  };

  useEffect(() => {
    registerHandleToggleAll();
    registerHandleDropDownLeaveToggle();
  }, [registerHandleToggleAll, registerHandleDropDownLeaveToggle]);

  return (
    <header
      className={`${NavbarStyle['navbar']} ${
        props.breadcrumbsCurrentPageDisplayName &&
        breadcrumbs &&
        breadcrumbs.length > 0
          ? NavbarStyle['has-breadcrumbs']
          : ''
      }`}>
      {/* Mobile version */}
      {props.canShow && (
        <div
          className={`${NavbarStyle['navbar-mobile']} ${
            showMobileMenu ? NavbarStyle['navbar-mobile-show'] : ''
          }`}>
          <div className={NavbarStyle['navbar-mobile-header']}>
            <Image
              quality={90}
              className={NavbarStyle['navbar-mobile-header-close']}
              src="/assets/images/icons/CloseIcon.svg"
              alt="Close mobile"
              width={32}
              height={32}
              onClick={() => toggleMobileMenu()}
            />
            {isAuthenticated && (
              <a
                className={NavbarStyle['shop-cart-icon-wrapper']}
                href={
                  router.locale !== 'en'
                    ? `/${router.locale}/account/orders`
                    : `/account/orders`
                }>
                <Image
                  quality={90}
                  src="/assets/images/icons/NavBarCartIcon.svg"
                  alt="Cart"
                  width={18}
                  height={18}
                />
                <span className={NavbarStyle['shop-cart-icon-indicator']}>
                  {appState.pendingOrdersCount}
                </span>
              </a>
            )}
            {!isAuthenticated && (
              <Button
                color={'minimal'}
                onClick={() => {
                  handleLoginClick();
                  router.push(
                    `${
                      router.locale !== 'en' ? `/${router.locale}` : ''
                    }/account/login`
                  );
                  toggleMobileMenu();
                }}>
                {props.items?.loginLabel}
              </Button>
            )}
          </div>
          <div className={NavbarStyle['navbar-mobile-scroller']}>
            <ul className={NavbarStyle['navbar-mobile-items']}>
              <li>
                <Button
                  color={'primary'}
                  onClick={() => {
                    goToContactUs();
                    toggleMobileMenu();
                  }}>
                  {props.items?.contactUsLabel}
                </Button>
              </li>
            </ul>
            {isAuthenticated && (
              <ul className={NavbarStyle['navbar-mobile-items']}>
                <li onClick={toggleAccountItems}>
                  <h3 className={NavbarStyle['large']}>
                    <span>{user?.user_info?.name || user?.email}</span>
                    <Image
                      quality={90}
                      className={`${NavbarStyle['dropdown-arrow']} ${
                        mobileMenuItemsCollapsedState.account
                          ? null
                          : NavbarStyle['chevron-down']
                      }`}
                      priority
                      src="/assets/images/icons/NavBarItemChevronIcon.svg"
                      alt="Services"
                      width={16}
                      height={10}
                    />
                  </h3>
                </li>
                {!mobileMenuItemsCollapsedState.account && (
                  <>
                    <li
                      className={NavbarStyle['navbar-mobile-sub-item']}
                      onClick={() => {
                        router.push('/account/profile');
                        toggleMobileMenu();
                      }}>
                      {props.items?.accountProfileLabel}
                    </li>
                    <li
                      className={NavbarStyle['navbar-mobile-sub-item']}
                      onClick={() => {
                        router.push('/account/orders');
                        toggleMobileMenu();
                      }}>
                      {props.items?.ordersLabel}
                    </li>
                    <li
                      className={NavbarStyle['navbar-mobile-sub-item']}
                      onClick={() => {
                        router.push('/account/portfolio');
                        toggleMobileMenu();
                      }}>
                      {props.items?.portfolioLabel}
                    </li>
                    <li
                      className={NavbarStyle['navbar-mobile-sub-item']}
                      onClick={() => {
                        router.push('/account/address-book');
                        toggleMobileMenu();
                      }}>
                      {props.items?.addressBookLabel}
                    </li>
                    <li
                      className={NavbarStyle['navbar-mobile-sub-item']}
                      onClick={() => {
                        props.onUserLogout;
                        toggleMobileMenu();
                      }}>
                      {props.items?.logoutLabel}
                    </li>
                  </>
                )}
              </ul>
            )}

            <hr className={NavbarStyle['navbar-mobile-divider']} />

            <ul className={NavbarStyle['navbar-mobile-items']}>
              {/* Services dropdown */}
              <li onClick={toggleServicesItems}>
                <h3>
                  <span>{props.items?.servicesLabel}</span>
                  <Image
                    quality={90}
                    className={`${NavbarStyle['dropdown-arrow']} ${
                      mobileMenuItemsCollapsedState.services
                        ? null
                        : NavbarStyle['chevron-down']
                    }`}
                    priority
                    src="/assets/images/icons/NavBarItemChevronIcon.svg"
                    alt="Services"
                    width={16}
                    height={10}
                  />
                </h3>
              </li>
              {!mobileMenuItemsCollapsedState.services && (
                <li
                  className={NavbarStyle['navbar-mobile-sub-item-group-title']}>
                  <a
                    href={props.items?.trademarkServicesGroupPath!}
                    onClick={() => toggleMobileMenu()}>
                    {props.items?.trademarkServicesGroupLabel}
                  </a>
                </li>
              )}
              {!mobileMenuItemsCollapsedState.services &&
                props.items?.trademarkServicesGroupItems.map((value, index) => {
                  return (
                    <li
                      className={NavbarStyle['navbar-mobile-sub-item']}
                      key={index}>
                      <a
                        href={getRoute(value.path)}
                        onClick={() => toggleMobileMenu()}>
                        {value.label}
                      </a>
                    </li>
                  );
                })}
              {!mobileMenuItemsCollapsedState.services && (
                <li
                  className={NavbarStyle['navbar-mobile-sub-item-group-title']}>
                  {props.items?.otherIpServicesGroupLabel}
                </li>
              )}
              {!mobileMenuItemsCollapsedState.services &&
                props.items?.otherIpServicesGroupItems.map((value, index) => {
                  return (
                    <li
                      className={NavbarStyle['navbar-mobile-sub-item']}
                      key={index}>
                      <a
                        href={getRoute(value.path)}
                        onClick={() => toggleMobileMenu()}>
                        {value.label}
                      </a>
                    </li>
                  );
                })}
              {/* Services dropdown */}

              {/* Pricing dropdown */}
              <li onClick={togglePricingItems}>
                <h3>
                  <span>{props.items?.pricingLabel}</span>
                  <Image
                    quality={90}
                    className={`${NavbarStyle['dropdown-arrow']} ${
                      mobileMenuItemsCollapsedState.pricing
                        ? null
                        : NavbarStyle['chevron-down']
                    }`}
                    priority
                    src="/assets/images/icons/NavBarItemChevronIcon.svg"
                    alt="Services"
                    width={16}
                    height={10}
                  />
                </h3>
              </li>
              {!mobileMenuItemsCollapsedState.pricing &&
                props.items?.pricingItems.map((value, index) => {
                  return (
                    <li
                      className={NavbarStyle['navbar-mobile-sub-item']}
                      key={index}>
                      <a
                        href={getRoute(value.path)}
                        onClick={() => toggleMobileMenu()}>
                        {value.label}
                      </a>
                    </li>
                  );
                })}
              {/* Pricing dropdown */}

              {/* Resources dropdown */}
              <li onClick={toggleResourcesItems}>
                <h3>
                  <span>{props.items?.resourcesLabel}</span>
                  <Image
                    quality={90}
                    className={`${NavbarStyle['dropdown-arrow']} ${
                      mobileMenuItemsCollapsedState.resources
                        ? null
                        : NavbarStyle['chevron-down']
                    }`}
                    priority
                    src="/assets/images/icons/NavBarItemChevronIcon.svg"
                    alt="Services"
                    width={16}
                    height={10}
                  />
                </h3>
              </li>
              {!mobileMenuItemsCollapsedState.resources && (
                <li
                  className={NavbarStyle['navbar-mobile-sub-item-group-title']}>
                  <a
                    href={props.items?.intellectualPropertyQuestionGroupPath!}
                    onClick={() => toggleMobileMenu()}>
                    {props.items?.intellectualPropertyQuestionGroupLabel}
                  </a>
                </li>
              )}
              {!mobileMenuItemsCollapsedState.resources &&
                props.items?.intellectualPropertyQuestionGroupItems.map(
                  (value, index) => {
                    return (
                      <li
                        className={NavbarStyle['navbar-mobile-sub-item']}
                        key={index}>
                        <a
                          href={getRoute(value.path)}
                          onClick={() => toggleMobileMenu()}>
                          {value.label}
                        </a>
                      </li>
                    );
                  }
                )}
              {!mobileMenuItemsCollapsedState.resources && (
                <li
                  className={NavbarStyle['navbar-mobile-sub-item-group-title']}>
                  {props.items?.otherResourcesGroupLabel}
                </li>
              )}
              {!mobileMenuItemsCollapsedState.resources &&
                props.items?.otherResourcesGroupItems.map((value, index) => {
                  return (
                    <li
                      className={NavbarStyle['navbar-mobile-sub-item']}
                      key={index}>
                      <a
                        href={getRoute(value.path)}
                        onClick={() => toggleMobileMenu()}>
                        {value.label}
                      </a>
                    </li>
                  );
                })}
              {/* Resources dropdown */}

              {/* About dropdown */}
              <li onClick={toggleAboutItems}>
                <h3>
                  <span>{props.items?.aboutUsLabel}</span>
                  <Image
                    quality={90}
                    className={`${NavbarStyle['dropdown-arrow']} ${
                      mobileMenuItemsCollapsedState.about
                        ? null
                        : NavbarStyle['chevron-down']
                    }`}
                    priority
                    src="/assets/images/icons/NavBarItemChevronIcon.svg"
                    alt="Services"
                    width={16}
                    height={10}
                  />
                </h3>
              </li>
              {!mobileMenuItemsCollapsedState.about &&
                props.items?.aboutUsItems.map((value, index) => {
                  return (
                    <li
                      className={NavbarStyle['navbar-mobile-sub-item']}
                      key={index}>
                      <a
                        href={getRoute(value.path)}
                        onClick={() => toggleMobileMenu()}>
                        {value.label}
                      </a>
                    </li>
                  );
                })}
              {/* About dropdown */}
            </ul>

            <hr className={NavbarStyle['navbar-mobile-divider']} />

            <ul className={NavbarStyle['navbar-mobile-items']}>
              <li onClick={toggleLanguageItems}>
                <h3>
                  <span>
                    <Image
                      quality={90}
                      className={
                        NavbarStyle['navbar-mobile-items-language-flag']
                      }
                      alt={`language-${
                        props.appLanguages.find(
                          (x) => x.twoLetterIso === props.appLocale
                        )?.twoLetterIso
                      }`}
                      src={`/assets/images/common/languages/${
                        props.appLanguages.find(
                          (x) => x.twoLetterIso === props.appLocale
                        )?.twoLetterIso
                      }.svg`}
                      height={24}
                      width={36}
                      sizes="24px"
                    />
                    {props.appLanguages &&
                      getSelectableLanguageLabel(
                        props.appLanguages.find(
                          (x) => x.twoLetterIso === props.appLocale
                        )
                      )}
                  </span>
                  <Image
                    quality={90}
                    className={`${NavbarStyle['dropdown-arrow']} ${
                      mobileMenuItemsCollapsedState.language
                        ? null
                        : NavbarStyle['chevron-down']
                    }`}
                    priority
                    src="/assets/images/icons/NavBarItemChevronIcon.svg"
                    alt="Services"
                    width={16}
                    height={10}
                  />
                </h3>
              </li>
              {!mobileMenuItemsCollapsedState.language &&
                props.appLanguages &&
                props.appLanguages.map((language, index) => {
                  if (language.twoLetterIso !== props.appLocale) {
                    return (
                      <li
                        className={NavbarStyle['navbar-mobile-sub-item']}
                        key={index}
                        onClick={() => {
                          handleLanguageChange(language);
                          toggleMobileMenu();
                        }}>
                        <Image
                          quality={90}
                          className={
                            NavbarStyle['navbar-mobile-items-language-flag']
                          }
                          alt={`language-${language.twoLetterIso}`}
                          src={`/assets/images/common/languages/${language.twoLetterIso}.svg`}
                          height={24}
                          width={36}
                          sizes="24px"
                        />
                        {getSelectableLanguageLabel(language)}
                      </li>
                    );
                  }
                })}
            </ul>

            <hr className={NavbarStyle['navbar-mobile-divider']} />

            <ul className={NavbarStyle['navbar-mobile-items']}>
              <li onClick={toggleCurrencyItems}>
                <h3>
                  <span>
                    {getCurrencySymbol(
                      props.appCurrencies.find(
                        (value) =>
                          value.isoCode === props.appCurrentCurrency?.isoCode
                      )?.isoCode
                    )}{' '}
                    {props.appCurrencies &&
                      props.appCurrencies.find(
                        (value) =>
                          value.isoCode === props.appCurrentCurrency?.isoCode
                      )?.isoCode}
                  </span>
                  <Image
                    quality={90}
                    className={`${NavbarStyle['dropdown-arrow']} ${
                      mobileMenuItemsCollapsedState.currency
                        ? null
                        : NavbarStyle['chevron-down']
                    }`}
                    priority
                    src="/assets/images/icons/NavBarItemChevronIcon.svg"
                    alt="Services"
                    width={16}
                    height={10}
                  />
                </h3>
              </li>
              {!mobileMenuItemsCollapsedState.currency &&
                props.appCurrencies &&
                props.appCurrencies.map((value, index) => {
                  if (value.isoCode !== props.appCurrentCurrency?.isoCode) {
                    return (
                      <li
                        className={NavbarStyle['navbar-mobile-sub-item']}
                        key={index}
                        onClick={() => {
                          handleCurrencyChange(value);
                          toggleMobileMenu();
                        }}>
                        {getCurrencySymbol(value.isoCode)} {value.isoCode}
                      </li>
                    );
                  }
                })}
            </ul>
          </div>
        </div>
      )}
      {/* Mobile version */}

      {/* Main navigation bar */}
      <nav className={NavbarStyle['navbar-content-block']}>
        {props.canShow && (
          <div
            className={`${NavbarStyle['navbar-content-slot']} ${NavbarStyle['navbar-content-slot-left']}`}>
            <a
              href={
                router.locale && router.locale !== 'en'
                  ? `/${router.locale}`
                  : '/'
              }
              aria-label="iGerent home">
              <Image
                quality={90}
                src="/assets/igerent-logo.svg"
                priority
                alt="iGerent"
                sizes="136px"
                width={136}
                height={25}
              />
            </a>

            <ul className={NavbarStyle['navbar-items']}>
              <li>
                <NavBarDropdown {...servicesProps} />
              </li>
              <li>
                <NavBarDropdown {...pricingProps} />
              </li>
              <li>
                <NavBarDropdown {...resourcesProps} />
              </li>
              <li>
                <NavBarDropdown {...aboutProps} />
              </li>
            </ul>

            <Image
              quality={90}
              className={NavbarStyle['navbar-mobile-burger']}
              src="/assets/images/icons/MenuIcon.svg"
              alt="Menu toggle mobile"
              width={50}
              height={50}
              onClick={() => toggleMobileMenu()}
            />
          </div>
        )}

        {props.canShow && (
          <div
            className={`${NavbarStyle['navbar-content-slot']} ${NavbarStyle['navbar-content-slot-right']}`}>
            <ul className={NavbarStyle['navbar-items']}>
              <li className={NavbarStyle['has-separator']}>
                <details
                  id="language-dropdown"
                  className={NavbarStyle['dropdown']}
                  onToggle={handleDropDownToggle}>
                  <summary
                    className={`${NavbarStyle['dropdown-summary']} ${NavbarStyle['navbar-link']}`}>
                    <div className={NavbarStyle['dropdown-button']}>
                      <span>
                        {props.appLanguages &&
                          getSelectableLanguageLabel(
                            props.appLanguages.find(
                              (x) => x.twoLetterIso === props.appLocale
                            )
                          )}
                      </span>
                      <Image
                        quality={90}
                        className={NavbarStyle['dropdown-arrow']}
                        priority
                        src="/assets/images/icons/NavBarItemChevronIcon.svg"
                        alt="Services"
                        width={12}
                        height={8}
                      />
                    </div>
                  </summary>
                  <div className={NavbarStyle['dropdown-content']}>
                    <ul>
                      {props.appLanguages?.map((language, index) => {
                        if (language.twoLetterIso !== props.appLocale) {
                          return (
                            <li
                              key={index}
                              onClick={() => handleLanguageChange(language)}>
                              <span>
                                {getSelectableLanguageLabel(language)}
                              </span>
                            </li>
                          );
                        }
                      })}
                    </ul>
                  </div>
                </details>
              </li>
              <li className={NavbarStyle['has-separator']}>
                <details
                  id="currency-dropdown"
                  className={NavbarStyle['dropdown']}
                  onToggle={handleDropDownToggle}>
                  <summary
                    className={`${NavbarStyle['dropdown-summary']} ${NavbarStyle['navbar-link']}`}>
                    <div className={NavbarStyle['dropdown-button']}>
                      <span>
                        {
                          props.appCurrencies.find(
                            (value) =>
                              value.isoCode ===
                              props.appCurrentCurrency?.isoCode
                          )?.isoCode
                        }
                      </span>
                      <Image
                        quality={90}
                        className={NavbarStyle['dropdown-arrow']}
                        priority
                        src="/assets/images/icons/NavBarItemChevronIcon.svg"
                        alt="Services"
                        width={12}
                        height={8}
                      />
                    </div>
                  </summary>
                  <div className={NavbarStyle['dropdown-content']}>
                    <ul>
                      {props.appCurrencies.map((value, index) => {
                        if (
                          value.isoCode !== props.appCurrentCurrency?.isoCode
                        ) {
                          return (
                            <li
                              key={index}
                              onClick={() => handleCurrencyChange(value)}>
                              <span>{value.isoCode}</span>
                            </li>
                          );
                        }
                      })}
                    </ul>
                  </div>
                </details>
              </li>
              <li>
                {isAuthenticated ? (
                  <details
                    id="user-account-dropdown"
                    className={NavbarStyle['dropdown']}
                    onToggle={handleDropDownToggle}>
                    <summary
                      className={`${NavbarStyle['dropdown-summary']} ${NavbarStyle['navbar-link']}`}>
                      <div className={NavbarStyle['dropdown-button']}>
                        <span>{user?.user_info?.name || user?.email}</span>
                        <Image
                          quality={90}
                          className={NavbarStyle['dropdown-arrow']}
                          src="/assets/images/icons/NavBarItemChevronIcon.svg"
                          alt={user?.user_info?.name || 'chevron'}
                          width={12}
                          height={8}
                        />
                      </div>
                    </summary>
                    <div className={NavbarStyle['dropdown-content']}>
                      <ul>
                        <li onClick={() => router.push('/account/profile')}>
                          <span>
                            <strong>{props.items?.accountProfileLabel}</strong>
                          </span>
                        </li>
                        <li onClick={() => router.push('/account/orders')}>
                          <span>{props.items?.ordersLabel}</span>
                        </li>
                        <li onClick={() => router.push('/account/portfolio')}>
                          <span>{props.items?.portfolioLabel}</span>
                        </li>
                        <li
                          onClick={() => router.push('/account/address-book')}>
                          <span>{props.items?.addressBookLabel}</span>
                        </li>
                        <li className={NavbarStyle['is-divider']}></li>
                        <li onClick={props.onUserLogout}>
                          <span>{props.items?.logoutLabel}</span>
                        </li>
                      </ul>
                    </div>
                  </details>
                ) : (
                  <a
                    className={NavbarStyle['navbar-link']}
                    href={`${
                      router.locale !== 'en' ? `/${router.locale}` : ''
                    }/account/login`}
                    onClick={handleLoginClick}>
                    {props.items?.loginLabel}
                  </a>
                )}
              </li>
              {isAuthenticated && (
                <li>
                  <a
                    className={NavbarStyle['shop-cart-icon-wrapper']}
                    href={
                      router.locale !== 'en'
                        ? `/${router.locale}/account/orders`
                        : `/account/orders`
                    }>
                    <Image
                      quality={90}
                      src="/assets/images/icons/NavBarCartIcon.svg"
                      priority
                      alt="Cart"
                      width={18}
                      height={18}
                    />
                    <span className={NavbarStyle['shop-cart-icon-indicator']}>
                      {appState.pendingOrdersCount}
                    </span>
                  </a>
                </li>
              )}
              <li>
                <Button
                  color={'primary'}
                  onClick={() => {
                    goToContactUs();
                  }}>
                  {props.items?.contactUsLabel}
                </Button>
              </li>
            </ul>
          </div>
        )}
      </nav>
      {/* Main navigation bar */}

      {/* Breadcrumbs */}
      {props.breadcrumbsCurrentPageDisplayName &&
        breadcrumbs &&
        breadcrumbs.length > 0 && (
          <nav
            className={NavbarStyle['navbar-breadcrumbs']}
            aria-label="Breadcrumb"
            itemScope
            itemType="https://schema.org/BreadcrumbList">
            <ol>
              {breadcrumbs.map((breadcrumb, index) => {
                const isLast = breadcrumbs && index === breadcrumbs.length - 1;

                if (!isLast && breadcrumb.path) {
                  return (
                    <li
                      key={`breadcrumb-${index}`}
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem">
                      <a href={getRoute(breadcrumb.path)} itemProp="item">
                        <span itemProp="name">{breadcrumb.label}</span>
                      </a>
                      <meta itemProp="position" content={`${index + 1}`} />
                    </li>
                  );
                } else {
                  return (
                    <li
                      key={`breadcrumb-last`}
                      aria-current="page"
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                      className={NavbarStyle['navbar-breadcrumbs-current']}>
                      <span itemProp="name">{breadcrumb.label}</span>
                      <meta itemProp="position" content={`${index + 1}`} />
                    </li>
                  );
                }
              })}
            </ol>
          </nav>
        )}
      {/* Breadcrumbs */}
    </header>
  );
};

export default NavBar;
